/**
 * The single place the SPA talks to the API.
 *
 * Everything goes through `request()` so three behaviours are guaranteed
 * rather than remembered per call site:
 *
 *   1. **Cookies are sent.** The session is an HttpOnly cookie; `fetch`
 *      omits credentials for same-origin requests by default in some
 *      configurations, and a request without the cookie is simply a 401.
 *   2. **Errors carry the server's Hebrew message.** The API answers
 *      `{detail: "..."}` with a message already phrased for a human. Throwing
 *      a bare "Request failed" would discard the useful half of the response.
 *   3. **A 401 is a session problem, not a request problem.** It is surfaced
 *      as a distinct flag so the shell can send the user to the login screen
 *      instead of showing a red toast on every panel at once.
 */
import type { Health } from './types'

export const API = '/api/v1'

export class ApiError extends Error {
  readonly status: number
  readonly isAuth: boolean
  readonly isForbidden: boolean

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.isAuth = status === 401
    this.isForbidden = status === 403
  }
}

async function messageFrom(response: Response): Promise<string> {
  // FastAPI uses `detail` for both our HTTPExceptions (a string) and its own
  // request-validation failures (a list of objects). Handle both, and never
  // let the error path itself throw — a parse failure here would replace a
  // useful 400 with an unhelpful "Unexpected token".
  try {
    const body = await response.json()
    const detail = body?.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      return detail
        .map((d) => (d?.loc ? `${d.loc.slice(1).join('.')}: ${d.msg}` : d?.msg))
        .filter(Boolean)
        .join('; ')
    }
    if (detail) return JSON.stringify(detail)
  } catch {
    /* fall through to the status text */
  }
  return response.statusText || `שגיאה ${response.status}`
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API}${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init.body instanceof FormData
        ? {}                       // let the browser set the multipart boundary
        : { 'Content-Type': 'application/json' }),
      ...init.headers,
    },
  })

  if (!response.ok) throw new ApiError(response.status, await messageFrom(response))
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  upload: <T>(path: string, file: File, field = 'file') => {
    const form = new FormData()
    form.append(field, file)
    return request<T>(path, { method: 'POST', body: form })
  },

  /**
   * Trigger a browser download of a CSV/db endpoint.
   *
   * Fetched rather than linked so the error path works: an `<a href>` to a
   * 403 renders the JSON error *as the downloaded file*, which is how a
   * permission problem turns into a corrupt-looking spreadsheet. The server's
   * `Content-Disposition` filename is honoured when present — it carries the
   * Hebrew project name via RFC 5987.
   */
  download: async (path: string, fallbackName: string) => {
    const response = await fetch(`${API}${path}`, { credentials: 'same-origin' })
    if (!response.ok) throw new ApiError(response.status, await messageFrom(response))

    const disposition = response.headers.get('content-disposition') ?? ''
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
    const plain = /filename="([^"]+)"/i.exec(disposition)
    const name = utf8 ? decodeURIComponent(utf8[1]) : plain?.[1] || fallbackName

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = name
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  },

  /**
   * Save a JSON endpoint's response as a file.
   *
   * Separate from `download` because these endpoints return a JSON *document*
   * with no `Content-Disposition` — the filename is the caller's to choose.
   * Pretty-printed on purpose: a task document is meant to be readable and
   * diffable by whoever receives it.
   */
  downloadJson: async (path: string, name: string) => {
    const body = await request<unknown>(path)
    const blob = new Blob([JSON.stringify(body, null, 2)],
                          { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = name
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  },
}

export const health = () => fetch('/healthz').then((r) => r.json() as Promise<Health>)
